import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import { useHierarchiesList } from '../../pages/hierarchies/hierarchyAccess'
import { useAssetTypeRegistry } from '../../pages/assets/useAssetTypeRegistry'
import { assetTypes } from '../../pages/assets/assetTypes'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import FieldType = com.eidu.sharedlib.entity.field.FieldType

export type AssistantFieldDefinition = {
  fieldId: string
  name: string
  type: string
  required: boolean
  referenceTypeIds?: string[]
}

export type AssistantEntityTypeSummary = {
  id: string
  name: string
  displayName: string
  kind: string
  fields: AssistantFieldDefinition[]
}

export type AssistantHierarchySummary = {
  id: string
  name: string
}

export type AssistantAssetTypeSummary = {
  entityTypeId: string
  entityTypeName: string
  assetTypes: Array<{
    id: string
    name: string
    payloadFormat: string
    contentLanguage?: string
    mediaType?: string
    mimeType?: string
  }>
}

export type AssistantAssetCatalogEntry = {
  id: string
  name: string
  payloadFormat: string
  contentLanguage?: string
  mediaType?: string
  mimeType?: string
}

export type AssistantEntitySummary = {
  id: string
  label?: string
  typeId?: string
  typeName?: string
}

export type AssistantEditorFieldSnapshot = {
  fieldId: string
  name: string
  type: string
  value: string
  required?: boolean
}

export type AssistantViewContext =
  | {
      kind: 'list'
      typeId: string
      typeName?: string
      visibleEntities: AssistantEntitySummary[]
    }
  | {
      kind: 'hierarchy'
      hierarchyId: string
      title?: string
      levels?: Array<{ levelName: string; typeId: string }>
      visibleEntities: AssistantEntitySummary[]
    }
  | {
      kind: 'entity-editor'
      entityId?: string
      typeId: string
      typeName?: string
      fields: AssistantEditorFieldSnapshot[]
    }
  | {
      kind: 'asset-editor'
      entityId?: string
      typeId: string
      typeName?: string
      assetType?: {
        id: string
        name: string
        payloadFormat?: string
        contentLanguage?: string
      }
      fields: AssistantEditorFieldSnapshot[]
      editorInstructions?: string
    }

export type AssistantEditorValueUpdate = {
  fieldId?: string
  fieldName?: string
  value: unknown
}

export type AssistantEditorAdapter = {
  applyEditorValues: (updates: AssistantEditorValueUpdate[]) => { applied: number; errors: string[] }
}

type AssistantContextValue = {
  organization?: { id: string; userEntityId?: string }
  entityTypes: AssistantEntityTypeSummary[]
  hierarchies: AssistantHierarchySummary[]
  assetTypesByEntityType: AssistantAssetTypeSummary[]
  assetTypeCatalog: AssistantAssetCatalogEntry[]
  currentView?: AssistantViewContext
  setCurrentView: (view?: AssistantViewContext) => void
  clearCurrentView: () => void
  editorAdapter?: AssistantEditorAdapter
  setEditorAdapter: (adapter?: AssistantEditorAdapter) => void
  entityTypesMap?: ReadonlyMap<EntityTypeId, EntityType | null>
}

const AssistantContext = createContext<AssistantContextValue>({
  entityTypes: [],
  hierarchies: [],
  assetTypesByEntityType: [],
  assetTypeCatalog: [],
  setCurrentView: () => undefined,
  clearCurrentView: () => undefined,
  setEditorAdapter: () => undefined,
})

const resolveEnumName = (value: unknown, map: Record<number, string>) => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && map[value]) return map[value]
  return String(value)
}

const resolveFieldTypeName = (fieldType: FieldType) =>
  resolveEnumName(fieldType, FieldType as unknown as Record<number, string>)

const resolveKindName = (kind: EntityTypeKind) =>
  resolveEnumName(kind, EntityTypeKind as unknown as Record<number, string>)

export const AssistantContextProvider = ({ children }: { children: ReactNode }) => {
  const entityTypeRepository = useContext(EntityTypesContext)
  const authContext = useContext<IAuthContext>(AuthContext)
  const { hierarchies } = useHierarchiesList()

  useEffect(() => {
    entityTypeRepository.getAll().then(() => undefined)
  }, [entityTypeRepository])

  const entityTypesMap = entityTypeRepository.useAll()

  const assetRegistry = useAssetTypeRegistry(entityTypesMap ?? undefined)

  const organization = useMemo(() => {
    const resolved = createAuthContext(authContext)
    if (!resolved) return undefined
    return { id: resolved.organizationId, userEntityId: resolved.userEntityId }
  }, [authContext])

  const entityTypes = useMemo<AssistantEntityTypeSummary[]>(() => {
    if (!entityTypesMap) return []
    return Array.from(entityTypesMap.values())
      .filter((type): type is EntityType => !!type)
      .map((type) => ({
        id: type.id.toString(),
        name: type.name,
        displayName: getTypeDisplayName(type.name),
        kind: resolveKindName(type.kind),
        fields: type.fields.asJsReadonlyArrayView().map((field) => ({
          fieldId: field.id.toString(),
          name: field.name,
          type: resolveFieldTypeName(field.type),
          required: field.required,
          referenceTypeIds: field.validReferenceTypes
            ? Array.from(field.validReferenceTypes.asJsReadonlySetView()).map((typeId) => typeId.toString())
            : undefined,
        })),
      }))
  }, [entityTypesMap])

  const hierarchiesSummary = useMemo(
    () => hierarchies.map((hierarchy) => ({ id: hierarchy.id, name: hierarchy.name })),
    [hierarchies]
  )

  const assetTypesByEntityType = useMemo(
    () =>
      assetRegistry.assetEntityTypes.map((config) => ({
        entityTypeId: config.entityTypeId.toString(),
        entityTypeName: config.entityTypeName,
        assetTypes: config.allowedTypes.map((type) => ({
          id: type.id,
          name: type.displayName,
          payloadFormat: type.payloadFormat,
          contentLanguage: type.contentLanguage,
          mediaType: type.mediaType,
          mimeType: type.mimeType,
        })),
      })),
    [assetRegistry.assetEntityTypes]
  )

  const assetTypeCatalog = useMemo(
    () =>
      assetTypes.map((type) => ({
        id: type.id,
        name: type.displayName,
        payloadFormat: type.payloadFormat,
        contentLanguage: type.contentLanguage,
        mediaType: type.mediaType,
        mimeType: type.mimeType,
      })),
    []
  )

  const [currentView, setCurrentViewState] = useState<AssistantViewContext | undefined>(undefined)
  const [editorAdapter, setEditorAdapterState] = useState<AssistantEditorAdapter | undefined>(undefined)

  const setCurrentView = useCallback((view?: AssistantViewContext) => {
    setCurrentViewState(view)
  }, [])

  const clearCurrentView = useCallback(() => {
    setCurrentViewState(undefined)
  }, [])

  const setEditorAdapter = useCallback((adapter?: AssistantEditorAdapter) => {
    setEditorAdapterState(adapter)
  }, [])

  const value = useMemo(
    () => ({
      organization,
      entityTypes,
      hierarchies: hierarchiesSummary,
      assetTypesByEntityType,
      assetTypeCatalog,
      currentView,
      setCurrentView,
      clearCurrentView,
      editorAdapter,
      setEditorAdapter,
      entityTypesMap,
    }),
    [
      organization,
      entityTypes,
      hierarchiesSummary,
      assetTypesByEntityType,
      assetTypeCatalog,
      currentView,
      setCurrentView,
      clearCurrentView,
      editorAdapter,
      setEditorAdapter,
      entityTypesMap,
    ]
  )

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}

export const useAssistantContext = () => useContext(AssistantContext)
